import 'twin.macro';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Input, {
  Props as InputProps,
} from '@teetsh/app/src/components/Form/Input2';
import { Transition } from '@headlessui/react';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { useInput } from 'react-day-picker';
import DatePicker from '@teetsh/app/src/components/Form/DatePicker';
import PortalWrapper from '@teetsh/app/src/components/PortalWrapper';
import parse from '@teetsh/app/src/utils/time/parse';
import { frenchDatePatternWithSlashes } from '@teetsh/app/src/utils/time/datePatterns';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import isSameDay from '@teetsh/app/src/utils/time/isSameDay';
import { UserLocale } from '@teetsh/app/src/types/user';
import { useTranslation } from 'react-i18next';

export interface Props {
  value?: Date | null;
  onChange: (v?: Date | null) => void;
  canBeNull?: boolean;
  sundayStart?: boolean;
  disabledDays?: number[];
  disabledFct?: (d: Date) => boolean;
  extraLarge?: boolean;
  portal?: boolean;
  dots?: Date[];
  openOnMount?: boolean;
  onClickOutside?: (event: any) => void;
  className?: string;
  userLocale: UserLocale;
  placeholder?: string;
  id: string;
  autoFocus?: boolean;
  fixedWeeks?: boolean;
}

const WrapperInput = forwardRef((props: InputProps, ref) => {
  const { onChange } = props;
  return (
    <Input {...props} autoComplete="off" onChange={onChange} inputRef={ref} />
  );
});

function fixDateYear(year: number): number {
  if (year < 100) {
    // As of 2024, users type the year with 2 digits thinking it's 20XX
    return year + 2000;
  }
  if (year < 1915) {
    // Before 1911 in France there was a weird time offset that causes infinite loop in AutoSave and parse errors
    // We don't see any reason why someone would want a date before 1915 🤷
    return 1915;
  }
  if (year > 9999) {
    // Prevent parsing issue (max year is 9999 in golang)
    return 9999;
  }
  return year;
}

function fixDate(date: Date): Date {
  const fixedYear = fixDateYear(date.getFullYear());
  if (fixedYear !== date.getFullYear()) {
    return new Date(fixedYear, date.getMonth(), date.getDate());
  }
  return date;
}

export function DateInput(props: Props) {
  const {
    onChange,
    id,
    canBeNull,
    sundayStart,
    extraLarge,
    disabledDays = [],
    portal = true,
    autoFocus,
    userLocale,
    className,
    openOnMount,
    placeholder,
    fixedWeeks,
  } = props;
  const value = canBeNull && props.value === null ? undefined : props.value;
  const { t } = useTranslation(['common']);
  const datePattern =
    t('common:datePattern.dd/MM/yyyy') || frenchDatePatternWithSlashes;
  const { inputProps, dayPickerProps, setSelected } = useInput({
    defaultSelected: value === null ? undefined : value,
    format: datePattern,
    required: !canBeNull,
  });

  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(2), flip(), shift()],
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
  });
  const handleClickOutside = event => {
    if (refs.floating && refs.floating.current) {
      if (
        !refs.floating.current.contains(event.target) &&
        !(inputRef.current as any).contains(event.target)
      ) {
        setIsOpen(false);
        if (props.onClickOutside) {
          return props.onClickOutside(event);
        }
      }
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return () => null;
  });

  useEffect(() => {
    if (inputRef?.current && openOnMount) {
      (inputRef?.current as any)?.focus();
    }
  }, []);

  const modifiers: any = {
    selected: value,
    disabled: {
      daysOfWeek: disabledDays,
    },
  };
  return (
    <div>
      <div ref={refs.setReference}>
        <WrapperInput
          ref={inputRef}
          {...inputProps}
          autoFocus={autoFocus}
          value={inputProps.value as string}
          onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
            const { key } = event;
            if (key === 'Enter') {
              event.preventDefault();
              if (inputRef.current) {
                (inputRef.current as any).blur();
              }
              if (inputProps.onBlur) {
                inputProps.onBlur(event as any);
              }
              setTimeout(() => {
                setIsOpen(false);
              }, 100);
            }
          }}
          onClick={() => {
            setIsOpen(true);
          }}
          onFocus={e => {
            if (inputProps.onFocus) {
              inputProps.onFocus(e);
            }
            setIsOpen(true);
          }}
          onBlur={e => {
            const parsedDate = parse(e.target.value, datePattern);
            if (canBeNull && e.target.value === '') {
              setSelected(undefined);
              onChange(null);
              return;
            }
            if (!isNaN(parsedDate.getTime())) {
              const parsedInputValue = parse(
                inputProps?.value as string,
                datePattern,
              );
              const newDate = fixDate(parsedDate);
              // if the input value is the same as the parsed date, we don't want to change the value
              // this is probably because the user is simply changing the month on
              // the datepicker
              if (
                isSameDay(newDate, parsedInputValue) &&
                props.value &&
                isSameDay(newDate, props.value)
              ) {
                return;
              }
              setSelected(newDate);
              onChange(newDate);
            }

            if (inputProps.onBlur) {
              inputProps.onBlur(e);
            }
          }}
          id={id}
          extraLarge={extraLarge}
          placeholder={placeholder}
          className={className}
        />
      </div>
      <PortalWrapper portal={portal}>
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 50,
          }}
          onFocus={() => {
            if (inputRef.current) {
              (inputRef.current as any).focus();
            }
          }}
        >
          <Transition
            show={isOpen}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <div tw="bg-white rounded-xl">
              <DatePicker
                {...dayPickerProps}
                {...modifiers}
                userLocale={userLocale}
                fixedWeeks={fixedWeeks}
                disabledDays={disabledDays}
                disabledFct={props.disabledFct}
                value={dayPickerProps.selected}
                mode="single"
                sundayStart={sundayStart}
                onChange={d => {
                  // If date A is already selected and we reclick on it, the onChange returns an undefined
                  // we don't want to reset to date so we skip
                  if (d) {
                    setSelected(d);
                    onChange(d);
                  }
                }}
              />
            </div>
          </Transition>
        </div>
      </PortalWrapper>
    </div>
  );
}

export type WrapperProps = Props & {
  label?: string;
  labelInfo?: string;
  error?: string;
  withoutMargin?: boolean;
};

export default function DateInput2(props: WrapperProps) {
  const { label, labelInfo, error, withoutMargin, ...rest } = props;
  if (label || labelInfo) {
    return (
      <FormGroup
        id={props.id}
        label={label}
        labelInfo={labelInfo}
        error={error}
        withoutMargin={withoutMargin}
      >
        <DateInput {...rest} />
      </FormGroup>
    );
  }
  return <DateInput {...rest} />;
}
